import { useState, useEffect, useRef, useCallback } from 'react';

// easeInOutQuart smooth scroll — consistent with CourseModal
const smoothScrollTo = (element, to, duration = 380) => {
    if (!element) return;
    const start = element.scrollTop;
    const change = to - start;
    if (Math.abs(change) < 2) return;
    const startTime = performance.now();
    const ease = (t) => t < 0.5 ? 8*t*t*t*t : 1 - Math.pow(-2*t + 2, 4) / 2;
    const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        element.scrollTop = start + change * ease(progress);
        if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
};

// Scroll a child element into view within its scrollable parent using custom easing
const smoothScrollIntoView = (element, offset = 80) => {
    if (!element) return;
    // Find the nearest scrollable ancestor
    let parent = element.parentElement;
    while (parent && parent !== document.body) {
        const { overflowY } = window.getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll') break;
        parent = parent.parentElement;
    }
    if (!parent || parent === document.body) {
        // Fallback: scroll the window
        const rect = element.getBoundingClientRect();
        smoothScrollTo({ get scrollTop() { return window.scrollY; }, set scrollTop(v) { window.scrollTo(0, v); } }, window.scrollY + rect.top - offset, 380);
        return;
    }
    const parentRect = parent.getBoundingClientRect();
    const elemRect = element.getBoundingClientRect();
    const target = parent.scrollTop + (elemRect.top - parentRect.top) - offset;
    smoothScrollTo(parent, target, 380);
};
import { useCourse } from '../../../context/CourseContext';
import { useQuiz } from '../../../context/QuizContext';
import courseService from '../../../services/courseService';
import { preferencesService } from '../../../services/api';
import ModuleButton from '../../courses/components/ModuleButton';
import CourseModal from '../../courses/components/CourseModal';
import { Loader2, HelpCircle, ChevronDown, Filter, Star } from 'lucide-react';
import QuizCard from './QuizCard';
import CourseCard from './CourseCard';
import RevisionSheetBanner from './RevisionSheetBanner';
import ModeSelectionModal from './ModeSelectionModal';
import ChapterGridTooltip from './ChapterGridTooltip';
import { userKey } from '../../../utils/userStorage';
import { formatTotalTime, getCourseTitle, getCourseType, getGroupProgress, getGroupProgressSplit, getGroupTotalTime } from './courseUtils';
import useFavorites from '../../../hooks/dashboard/useFavorites';

const CoursesTab = ({ levelId, language, hasCourseAccess = true, onPurchase }) => {
    const { modules, loadModules, loading: modulesLoading, error: modulesError } = useCourse();
    const { startQuiz, checkExistingSession, quizId } = useQuiz();
    const [showQuizModeModal, setShowQuizModeModal] = useState(null); // course object or null
    const [selectedModule, setSelectedModule] = useState(null);
    const [courses, setCourses] = useState([]);
    const [moduleHasFullAccess, setModuleHasFullAccess] = useState(false);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [coursesError, setCoursesError] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [quizSessions, setQuizSessions] = useState({}); // {courseId: {mode, ...}}
    const [groupPdfs, setGroupPdfs] = useState([]); // [{groupName, labelFr, labelEn}]
    // Persistance du groupe actif par module (clé = "moduleId" en string)
    const [expandedGroupByModule, setExpandedGroupByModule] = useState(() => {
        try {
            // Read from main prefs first
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);
                if (prefs.lastCourseGroups && Object.keys(prefs.lastCourseGroups).length > 0) {
                    return prefs.lastCourseGroups;
                }
            }
            // Fallback to legacy key (migration)
            const saved = localStorage.getItem(userKey('courses_expandedGroupByModule'));
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });
    const expandedGroupRef = useRef(expandedGroupByModule); // Ref pour accès synchrone
    const groupRefs = useRef({});
    const moduleSyncTimeoutRef = useRef(null);
    const groupSyncTimeoutRef = useRef(null);

    // Toggle to show/hide course card stats (persisted in localStorage)
    const [showCourseStats, setShowCourseStats] = useState(() => {
        try {
            const saved = localStorage.getItem(userKey('nizus_show_course_stats'));
            return saved !== null ? JSON.parse(saved) : true;
        } catch {
            return true;
        }
    });

    // Toggle course stats visibility and persist to localStorage
    const toggleCourseStats = useCallback(() => {
        setShowCourseStats(prev => {
            const newValue = !prev;
            try {
                localStorage.setItem(userKey('nizus_show_course_stats'), JSON.stringify(newValue));
            } catch {}
            return newValue;
        });
    }, []);

    // Filter to show only free content (only relevant when level is not unlocked)
    const [showFreeOnly, setShowFreeOnly] = useState(false);

    // Filter to show only incomplete courses (progress < 100%)
    const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);

    // Favorites
    const { isFavoriteCourse, toggleFavoriteCourse } = useFavorites();
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

    // Track the last opened course to highlight it in the list
    const [lastOpenedCourseId, setLastOpenedCourseId] = useState(() => {
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);
                return prefs.lastOpenedCourse?.[levelId] || null;
            }
        } catch {}
        return null;
    });

    // Update lastOpenedCourseId when levelId changes
    useEffect(() => {
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            if (raw) {
                const prefs = JSON.parse(raw);
                setLastOpenedCourseId(prefs.lastOpenedCourse?.[levelId] || null);
            }
        } catch {}
    }, [levelId]);

    // Tracks the courseId we already auto-scrolled to (prevents re-scrolling on
    // every render if the user has manually scrolled away). Reset when the
    // active course changes.
    const activeCourseScrolledRef = useRef(null);

    useEffect(() => {
        if (activeCourseScrolledRef.current && activeCourseScrolledRef.current !== lastOpenedCourseId) {
            activeCourseScrolledRef.current = null;
        }
    }, [lastOpenedCourseId]);

    // Sync ref avec state
    useEffect(() => {
        expandedGroupRef.current = expandedGroupByModule;
    }, [expandedGroupByModule]);

    // Cleanup sync timeouts on unmount
    useEffect(() => {
        return () => {
            if (moduleSyncTimeoutRef.current) clearTimeout(moduleSyncTimeoutRef.current);
            if (groupSyncTimeoutRef.current) clearTimeout(groupSyncTimeoutRef.current);
        };
    }, []);

    // Helper to get module key as string
    const getModuleKey = (moduleId) => String(moduleId);

    // Helper: save group state to main prefs + debounced server sync
    const saveGroupToPrefs = (newState) => {
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            prefs.lastCourseGroups = newState;
            localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));

            if (groupSyncTimeoutRef.current) clearTimeout(groupSyncTimeoutRef.current);
            groupSyncTimeoutRef.current = setTimeout(() => {
                preferencesService.save({ lastCourseGroups: newState }).catch(() => {});
            }, 1000);
        } catch { /* ignore */ }
    };

    // Load modules when level changes
    useEffect(() => {
        if (levelId) {
            setSelectedModule(null);
            setCourses([]);
            loadModules(levelId);
        }
    }, [levelId]);

    // Auto-select saved module or first module when modules load
    useEffect(() => {
        if (modules && modules.length > 0 && !selectedModule) {
            // Try to restore saved module for this level
            try {
                const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
                if (raw) {
                    const prefs = JSON.parse(raw);
                    const savedModule = prefs.lastCourseModules?.[levelId];
                    if (savedModule && modules.some(m => m.idModule === savedModule)) {
                        setSelectedModule(savedModule);
                        return;
                    }
                }
            } catch { /* ignore */ }
            setSelectedModule(modules[0].idModule);
        }
    }, [modules]);

    // When free filter is toggled on, ensure selected module is a free one
    useEffect(() => {
        if (showFreeOnly && modules && modules.length > 0 && selectedModule) {
            const currentMod = modules.find(m => m.idModule === selectedModule);
            if (currentMod && !currentMod.isFree) {
                const firstFree = modules.find(m => m.isFree);
                if (firstFree) setSelectedModule(firstFree.idModule);
            }
        }
    }, [showFreeOnly]);

    // Load courses when module is selected
    useEffect(() => {
        if (selectedModule) {
            loadCourses(selectedModule);
        }
    }, [selectedModule]);

    // Auto-expand saved group or first group when courses are loaded
    useEffect(() => {
        if (!courses || courses.length === 0 || !selectedModule || coursesLoading) {
            return;
        }

        // CRITICAL: Check that courses belong to the selected module
        // This prevents using old courses data when switching modules
        const coursesModuleId = courses[0]?.nroModule;
        if (coursesModuleId !== selectedModule) {
            return;
        }

        // Check if courses have groups
        const hasGroups = courses.some(course =>
            course.groupeLibelle_fr || course.groupeLibelle_en
        );

        // Ungrouped case: just scroll to the last opened course card if any
        if (!hasGroups) {
            if (lastOpenedCourseId && activeCourseScrolledRef.current !== lastOpenedCourseId) {
                setTimeout(() => {
                    const el = document.querySelector(`[data-course-id="${lastOpenedCourseId}"]`);
                    if (el) {
                        smoothScrollIntoView(el, 120);
                        activeCourseScrolledRef.current = lastOpenedCourseId;
                    }
                }, 200);
            }
            return;
        }

        // Group courses to find available groups
        const groups = {};
        courses.forEach(course => {
            const groupKey = language === 'fr'
                ? (course.groupeLibelle_fr || 'other')
                : (course.groupeLibelle_en || 'other');

            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(course);
        });

        const sortedGroupNames = Object.keys(groups).sort();
        const moduleKey = getModuleKey(selectedModule);

        // Use ref to get current value (avoid dependency on state)
        const currentStateFromRef = expandedGroupRef.current;
        const currentGroup = currentStateFromRef[moduleKey];

        // Find if group exists
        const foundGroup = sortedGroupNames.find(g => g === currentGroup);

        // If we have a "last opened course" and it belongs to one of the available
        // groups, prefer scrolling directly to that card (and expand the group it
        // belongs to). Otherwise fall back to scrolling to the group header.
        const findGroupOf = (courseId) => {
            if (!courseId) return null;
            for (const [groupName, groupCourses] of Object.entries(groups)) {
                if (groupCourses.some(c => c.idCourse === courseId)) return groupName;
            }
            return null;
        };
        const targetGroup = findGroupOf(lastOpenedCourseId) || (currentGroup && foundGroup ? currentGroup : sortedGroupNames[0]);
        if (!targetGroup) return;

        // Make sure the target group is expanded
        const needsExpansion = currentStateFromRef[moduleKey] !== targetGroup;
        if (needsExpansion) {
            const newState = { ...currentStateFromRef, [moduleKey]: targetGroup };
            expandedGroupRef.current = newState;
            setExpandedGroupByModule(newState);
            saveGroupToPrefs(newState);
        }

        const wantCardScroll = lastOpenedCourseId
            && findGroupOf(lastOpenedCourseId) === targetGroup
            && activeCourseScrolledRef.current !== lastOpenedCourseId;

        setTimeout(() => {
            if (wantCardScroll) {
                const el = document.querySelector(`[data-course-id="${lastOpenedCourseId}"]`);
                if (el) {
                    smoothScrollIntoView(el, 120);
                    activeCourseScrolledRef.current = lastOpenedCourseId;
                    return;
                }
            }
            // Fallback: scroll to the group header
            if (groupRefs.current[targetGroup]) {
                smoothScrollIntoView(groupRefs.current[targetGroup]);
            }
        }, 200);
    }, [courses, language, selectedModule, coursesLoading, lastOpenedCourseId]);

    const loadCourses = async (moduleId) => {
        setCoursesLoading(true);
        setCoursesError(null);
        setCourses([]); // Clear old courses immediately
        setQuizSessions({}); // Clear old sessions
        setGroupPdfs([]); // Clear old PDFs
        try {
            const [data, pdfs] = await Promise.all([
                courseService.getCourses(moduleId),
                courseService.getGroupPdfs(moduleId),
            ]);
            const coursesList = data.courses || [];
            setCourses(coursesList);
            setModuleHasFullAccess(!!data.hasFullCourseAccess);
            setGroupPdfs(pdfs);
            // Load active quiz sessions for quiz courses
            const quizCourses = coursesList.filter(c => {
                const type = (c.typeCourse || c.type || '').toUpperCase();
                return c.hasQuiz || type === 'QUIZ' || type === 'QUIZZ';
            });
            if (quizCourses.length > 0) {
                const sessions = {};
                await Promise.all(quizCourses.map(async (c) => {
                    try {
                        const session = await courseService.getQuizSession(c.idCourse);
                        if (session) {
                            sessions[c.idCourse] = session;
                        }
                    } catch { /* no session */ }
                }));
                setQuizSessions(sessions);
            }
        } catch (err) {
            setCoursesError(err.message);
            console.error('Failed to load courses:', err);
        } finally {
            setCoursesLoading(false);
        }
    };

    // Refresh courses when quiz session ends (quizId goes from truthy to null)
    const prevQuizId = useRef(quizId);
    useEffect(() => {
        if (prevQuizId.current && !quizId && selectedModule) {
            loadCourses(selectedModule);
        }
        prevQuizId.current = quizId;
    }, [quizId]);

    // Toggle accordion group (mono-active mode: only one open at a time per module)
    const toggleGroup = (groupName) => {
        const moduleKey = getModuleKey(selectedModule);

        // IMPORTANT: Use ref for reading to ensure consistency
        const currentState = expandedGroupRef.current;
        const currentGroup = currentState[moduleKey];

        // If clicking on already expanded group, close it
        if (currentGroup === groupName) {
            const newState = { ...currentState };
            delete newState[moduleKey];

            // Update ref FIRST
            expandedGroupRef.current = newState;

            // Then update state and prefs
            setExpandedGroupByModule(newState);
            saveGroupToPrefs(newState);
            return;
        }

        // Otherwise, open this group (and save it for this module)
        const newState = {
            ...currentState,
            [moduleKey]: groupName
        };

        // Update ref FIRST
        expandedGroupRef.current = newState;

        // Then update state and prefs
        setExpandedGroupByModule(newState);
        saveGroupToPrefs(newState);

        // Scroll to the group after accordion animation completes (300ms transition)
        setTimeout(() => {
            if (groupRefs.current[groupName]) {
                smoothScrollIntoView(groupRefs.current[groupName]);
            }
        }, 350);
    };

    const handleModuleClick = (moduleId) => {
        // Only change if selecting a different module (no deselect allowed)
        if (selectedModule !== moduleId) {
            setSelectedModule(moduleId);

            // Save to prefs + debounced server sync
            try {
                const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
                const prefs = raw ? JSON.parse(raw) : {};
                const lastCourseModules = { ...prefs.lastCourseModules, [levelId]: moduleId };
                prefs.lastCourseModules = lastCourseModules;
                localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));

                if (moduleSyncTimeoutRef.current) clearTimeout(moduleSyncTimeoutRef.current);
                moduleSyncTimeoutRef.current = setTimeout(() => {
                    preferencesService.save({ lastCourseModules }).catch(() => {});
                }, 1000);
            } catch { /* ignore */ }
        }
    };

    const handleStartCourse = async (course) => {
        if (course.requiresUpgrade) {
            onPurchase();
            return;
        }
        // Track last opened course
        setLastOpenedCourseId(course.idCourse);
        try {
            const raw = localStorage.getItem(userKey('nizus_dashboard_prefs'));
            const prefs = raw ? JSON.parse(raw) : {};
            prefs.lastOpenedCourse = { ...(prefs.lastOpenedCourse || {}), [levelId]: course.idCourse };
            localStorage.setItem(userKey('nizus_dashboard_prefs'), JSON.stringify(prefs));
        } catch {}

        const isQuiz = getCourseType(course);
        if (isQuiz) {
            // Check for existing session to resume
            const existingSession = await checkExistingSession(course.idCourse);
            if (existingSession) {
                // Resume with the saved mode
                startQuiz(course.idCourse, existingSession.mode, {
                    fr: course.libelle_fr || course.titreCourse || course.titre || 'Sans titre',
                    en: course.libelle_en || course.titreCourse || course.titre || 'Untitled',
                });
            } else {
                // Show mode selection modal
                setShowQuizModeModal(course);
            }
            return;
        }
        setSelectedCourse(course);
        setShowModal(true);
    };

    const handleStartQuizWithMode = (mode) => {
        if (!showQuizModeModal) return;
        startQuiz(showQuizModeModal.idCourse, mode, {
            fr: showQuizModeModal.libelle_fr || showQuizModeModal.titreCourse || showQuizModeModal.titre || 'Sans titre',
            en: showQuizModeModal.libelle_en || showQuizModeModal.titreCourse || showQuizModeModal.titre || 'Untitled',
        });
        setShowQuizModeModal(null);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCourse(null);
        // Refresh courses to update progress
        if (selectedModule) {
            loadCourses(selectedModule);
        }
    };


    // Group courses by groupeLibelle if available
    // Filters work at the PAIR level: if a quiz matches, its parent course stays visible too
    const groupedCourses = () => {
        if (!courses || courses.length === 0) return null;

        // Pair courses with quizzes first
        const allPairs = pairCoursesWithQuizzes(courses);

        // Filter at pair level — a pair passes if either course or quiz matches
        const matchesFilter = (c) => c && (
            (!showFreeOnly || !c.requiresUpgrade) &&
            (!showIncompleteOnly || (c.progress || 0) < 100) &&
            (!showFavoritesOnly || isFavoriteCourse(c.idCourse))
        );

        const filteredPairs = allPairs.filter(pair =>
            matchesFilter(pair.course) || matchesFilter(pair.quiz)
        );

        if (filteredPairs.length === 0) return null;

        // Flatten pairs back to a flat list (preserving order) for grouping
        const filtered = [];
        filteredPairs.forEach(pair => {
            if (pair.course) filtered.push(pair.course);
            if (pair.quiz) filtered.push(pair.quiz);
        });

        // Check if courses have groups
        const hasGroups = filtered.some(course =>
            course.groupeLibelle_fr || course.groupeLibelle_en
        );

        if (!hasGroups) {
            return { ungrouped: filtered };
        }

        // Group courses by groupeLibelle
        const groups = {};
        filtered.forEach(course => {
            const groupKey = language === 'fr'
                ? (course.groupeLibelle_fr || 'other')
                : (course.groupeLibelle_en || 'other');

            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(course);
        });

        return groups;
    };


    // Pair each non-quiz course with its following quiz (if any)
    const pairCoursesWithQuizzes = (list) => {
        const pairs = [];
        let i = 0;
        while (i < list.length) {
            const current = list[i];
            if (getCourseType(current)) {
                // Standalone quiz (no preceding course) — render alone
                pairs.push({ course: null, quiz: current });
                i++;
            } else {
                // Non-quiz course — check if next item is a quiz
                const next = list[i + 1];
                if (next && getCourseType(next)) {
                    pairs.push({ course: current, quiz: next });
                    i += 2;
                } else {
                    pairs.push({ course: current, quiz: null });
                    i++;
                }
            }
        }
        return pairs;
    };

    const renderCourseCard = (course, index) => {
        const isQuiz = getCourseType(course);

        if (isQuiz) {
            return (
                <QuizCard
                    key={course.idCourse}
                    course={course}
                    index={index}
                    language={language}
                    showStats={showCourseStats}
                    activeQuizSession={quizSessions[course.idCourse] || null}
                    isFavorite={isFavoriteCourse(course.idCourse)}
                    onToggleFavorite={toggleFavoriteCourse}
                    onStart={handleStartCourse}
                    onPurchase={onPurchase}
                />
            );
        }

        return (
            <div key={course.idCourse} data-course-id={course.idCourse}>
                <CourseCard
                    course={course}
                    index={index}
                    language={language}
                    showStats={showCourseStats}
                    isActive={lastOpenedCourseId === course.idCourse}
                    isFavorite={isFavoriteCourse(course.idCourse)}
                    onToggleFavorite={toggleFavoriteCourse}
                    onStart={handleStartCourse}
                    onPurchase={onPurchase}
                />
            </div>
        );
    };

    const renderPairedCards = (list) => {
        const pairs = pairCoursesWithQuizzes(list);
        return pairs.map((pair, idx) => {
            // Standalone quiz (no parent course)
            if (!pair.course) {
                return renderCourseCard(pair.quiz, idx);
            }
            // Course without quiz
            if (!pair.quiz) {
                return renderCourseCard(pair.course, idx);
            }

            // Course + inline quiz in a single card
            const quiz = pair.quiz;
            const quizLocked = quiz.requiresUpgrade;
            const quizProgress = quiz.progress || 0;
            const activeQuizSession = quizSessions[quiz.idCourse] || null;
            const isQuizFavorite = isFavoriteCourse(quiz.idCourse);
            const isActive = lastOpenedCourseId === pair.course.idCourse;
            const courseLocked = pair.course.requiresUpgrade;

            return (
                <div
                    key={pair.course.idCourse}
                    data-course-id={pair.course.idCourse}
                    className={`bg-white dark:bg-[#121212] rounded-2xl border transition-all shadow-sm dark:shadow-none ${
                        courseLocked
                            ? 'border-gray-200 dark:border-white/5'
                            : isActive
                                ? 'border-blue-400 dark:border-blue-500/60 ring-2 ring-blue-400/30 dark:ring-blue-500/20'
                                : 'border-gray-200 dark:border-white/5'
                    }`}
                >
                    {/* Course section */}
                    <CourseCard
                        course={pair.course}
                        index={idx}
                        language={language}
                        showStats={showCourseStats}
                        isActive={isActive}
                        isFavorite={isFavoriteCourse(pair.course.idCourse)}
                        onToggleFavorite={toggleFavoriteCourse}
                        onStart={handleStartCourse}
                        onPurchase={onPurchase}
                        embedded
                    />

                    {/* Purple separator */}
                    <div className="mx-4 md:mx-6 border-t-2 border-purple-300/60 dark:border-purple-500/30" />

                    {/* Compact inline quiz section */}
                    <div className={`p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 ${
                        quizLocked ? 'opacity-60 grayscale pointer-events-none' : ''
                    }`}>
                        {/* Quiz title row */}
                        <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
                            {toggleFavoriteCourse && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); toggleFavoriteCourse(quiz.idCourse); }}
                                    className="flex-shrink-0 p-0.5 transition-colors"
                                    title={isQuizFavorite ? (language === 'fr' ? 'Retirer des favoris' : 'Remove from favorites') : (language === 'fr' ? 'Ajouter aux favoris' : 'Add to favorites')}
                                >
                                    <Star size={16} className={isQuizFavorite ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600 hover:text-amber-300 dark:hover:text-amber-500'} />
                                </button>
                            )}
                            <span className="text-base md:text-lg font-bold text-gray-900 dark:text-white truncate">
                                {getCourseTitle(quiz, language)}
                            </span>
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-purple-500 text-white shadow-lg shadow-purple-500/30 flex-shrink-0">
                                Quiz
                            </span>
                            {quizLocked && (
                                <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 flex-shrink-0">
                                    Premium
                                </span>
                            )}
                        </div>

                        {/* Compact stats — one line with tooltips */}
                        {showCourseStats && (
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 flex-1 min-w-0">
                                {/* Progression */}
                                <ChapterGridTooltip
                                    chapterStatuses={quiz.chapterStatuses || []}
                                    language={language}
                                    isQuiz={true}
                                    hasActiveSession={quiz.hasActiveSession}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-blue-500 dark:text-blue-400">Prog</span>
                                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${quizProgress}%` }} />
                                        </div>
                                        <span className="text-xs font-black text-gray-900 dark:text-white">{quizProgress}%</span>
                                    </div>
                                </ChapterGridTooltip>
                                {/* Compréhension */}
                                <ChapterGridTooltip
                                    chapterStatuses={quiz.chapterStatuses || []}
                                    language={language}
                                    isQuiz={true}
                                    mode="comprehension"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-purple-500 dark:text-purple-400">Comp</span>
                                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${quiz.comprehension || 0}%` }} />
                                        </div>
                                        <span className="text-xs font-black text-gray-900 dark:text-white">{quiz.comprehension != null ? `${quiz.comprehension}%` : '-'}</span>
                                    </div>
                                </ChapterGridTooltip>
                                {/* Réussite */}
                                <ChapterGridTooltip
                                    chapterStatuses={[]}
                                    language={language}
                                    customContent={quiz.totalAnswersGiven > 0 ? (
                                        <>
                                            <p className="text-[10px] text-gray-300 mb-2 leading-relaxed">
                                                {language === 'fr'
                                                    ? 'Nombre total de réponses correctes sur l\'ensemble de tes tentatives de quiz.'
                                                    : 'Total correct answers across all your quiz attempts.'}
                                            </p>
                                            <div className="flex items-baseline gap-2 mb-1">
                                                <span className="text-lg font-black text-white">{quiz.totalCorrectAnswers}</span>
                                                <span className="text-[10px] text-gray-400">/</span>
                                                <span className="text-lg font-black text-white">{quiz.totalAnswersGiven}</span>
                                                <span className="text-[10px] text-gray-400">{language === 'fr' ? 'réponses' : 'answers'}</span>
                                            </div>
                                            <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden mb-1">
                                                <div className="h-full bg-rose-500 rounded-full" style={{ width: `${quiz.successRate || 0}%` }} />
                                            </div>
                                            <p className="text-[10px] font-bold text-rose-400">{quiz.successRate}% {language === 'fr' ? 'de réussite' : 'success rate'}</p>
                                        </>
                                    ) : null}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-rose-500 dark:text-rose-400">
                                            {language === 'fr' ? 'Réus' : 'Pass'}
                                        </span>
                                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-rose-500 rounded-full" style={{ width: `${quiz.successRate || 0}%` }} />
                                        </div>
                                        <span className="text-xs font-black text-gray-900 dark:text-white">
                                            {quiz.totalAnswersGiven > 0 ? `${quiz.totalCorrectAnswers}/${quiz.totalAnswersGiven}` : '-'}
                                        </span>
                                    </div>
                                </ChapterGridTooltip>
                                {/* Dernier Score */}
                                <ChapterGridTooltip
                                    chapterStatuses={quiz.chapterStatuses || []}
                                    language={language}
                                    isQuiz={true}
                                    mode="score"
                                    scoreAnswers={quiz.lastSessionAnswers}
                                    correctAnswers={quiz.correctAnswers}
                                    scoreDescription={language === 'fr' ? 'Résultat par question de ta dernière session.' : 'Per-question result from your last session.'}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-amber-500 dark:text-amber-400">Score</span>
                                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${quiz.lastScore || 0}%` }} />
                                        </div>
                                        <span className="text-xs font-black text-gray-900 dark:text-white">{quiz.lastScore != null ? `${quiz.lastScore}%` : '-'}</span>
                                    </div>
                                </ChapterGridTooltip>
                                {/* Meilleur Score */}
                                <ChapterGridTooltip
                                    chapterStatuses={quiz.chapterStatuses || []}
                                    language={language}
                                    isQuiz={true}
                                    mode="score"
                                    scoreAnswers={quiz.bestSessionAnswers}
                                    correctAnswers={quiz.correctAnswers}
                                    scoreDescription={language === 'fr' ? 'Résultat par question de ta meilleure session.' : 'Per-question result from your best session.'}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-emerald-500 dark:text-emerald-400">Best</span>
                                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${quiz.bestScore || 0}%` }} />
                                        </div>
                                        <span className="text-xs font-black text-gray-900 dark:text-white">{quiz.bestScore != null ? `${quiz.bestScore}%` : '-'}</span>
                                    </div>
                                </ChapterGridTooltip>
                                {/* Temps */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                                        {language === 'fr' ? 'Temps' : 'Time'}
                                    </span>
                                    <span className="text-xs font-black text-gray-900 dark:text-white">{formatTotalTime(quiz.totalTime) || '0min'}</span>
                                </div>
                            </div>
                        )}

                        {/* Action button */}
                        <div className="flex-shrink-0">
                            {quizLocked ? (
                                <button
                                    onClick={onPurchase}
                                    className="pointer-events-auto px-6 py-2.5 rounded-xl font-black text-xs shadow-xl transition hover:scale-105 active:scale-95 flex items-center gap-2 whitespace-nowrap text-amber-700 dark:text-amber-300 bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-500/20 dark:to-amber-600/20 shadow-amber-500/20 border border-amber-300 dark:border-amber-500/30"
                                >
                                    {language === 'fr' ? 'Débloquer' : 'Unlock'}
                                </button>
                            ) : (
                                <button
                                    onClick={() => handleStartCourse(quiz)}
                                    className="px-6 py-2.5 rounded-xl font-black text-xs text-white shadow-xl transition bg-gradient-to-r from-blue-600 to-blue-700 hover:scale-105 active:scale-95 flex items-center gap-2 whitespace-nowrap"
                                >
                                    {activeQuizSession
                                        ? (language === 'fr' ? 'Continuer' : 'Continue')
                                        : (quiz.progress > 0 || quiz.lastScore != null
                                            ? (language === 'fr' ? 'Recommencer' : 'Restart')
                                            : (language === 'fr' ? 'Commencer' : 'Start'))}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            );
        });
    };

    // Loading state for modules
    if (modulesLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
            </div>
        );
    }

    // Error state for modules
    if (modulesError) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center max-w-2xl mx-auto">
                <p className="text-red-600 dark:text-red-400">
                    {language === 'fr' ? 'Erreur lors du chargement des modules' : 'Error loading modules'}
                </p>
            </div>
        );
    }

    // No modules
    if (!modules || modules.length === 0) {
        return (
            <div className="bg-white dark:bg-[#121212] rounded-2xl p-8 border border-gray-200 dark:border-white/5 text-center max-w-2xl mx-auto">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {language === 'fr' ? 'Aucun module disponible' : 'No modules available'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                    {language === 'fr'
                        ? 'Aucun cours n\'est disponible pour ce niveau actuellement.'
                        : 'No courses are available for this level currently.'}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-12 max-w-7xl mx-auto pb-24 px-4 relative">
            {/* Module Selector (like categories) - Horizontal slider on mobile */}
            <div data-help="courses-modules" className="-mx-6 md:mx-0">
                <div className="flex md:grid md:grid-cols-5 gap-3 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none no-scrollbar px-6 md:px-0 pb-2 md:pb-0">
                    {(showFreeOnly ? modules.filter(m => m.isFree) : modules).map((module) => (
                        <div key={module.idModule} className="flex-shrink-0 md:flex-shrink snap-center w-[140px] md:w-auto">
                            <ModuleButton
                                module={module}
                                isSelected={selectedModule === module.idModule}
                                onClick={handleModuleClick}
                                language={language}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Courses List (like exams) */}
            <div data-help="courses-list">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">
                        {modules.find(m => m.idModule === selectedModule)?.[language === 'fr' ? 'libelle_fr' : 'libelle_en'] ||
                         (language === 'fr' ? 'Cours' : 'Courses')}
                    </h2>
                    <div className="flex items-center gap-2">
                    {/* Favorites filter */}
                    <button
                        onClick={() => setShowFavoritesOnly(prev => !prev)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            showFavoritesOnly
                                ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-500/30'
                                : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                        }`}
                        title={showFavoritesOnly
                            ? (language === 'fr' ? 'Afficher tous les cours' : 'Show all courses')
                            : (language === 'fr' ? 'Afficher uniquement les favoris' : 'Show favorites only')
                        }
                    >
                        <Star size={14} className={showFavoritesOnly ? 'fill-current' : ''} />
                        <span className="hidden sm:inline">{language === 'fr' ? 'Favoris' : 'Favorites'}</span>
                    </button>
                    {/* Incomplete filter - always visible */}
                    <button
                        onClick={() => setShowIncompleteOnly(prev => !prev)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            showIncompleteOnly
                                ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-500/30'
                                : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                        }`}
                        title={showIncompleteOnly
                            ? (language === 'fr' ? 'Afficher tous les cours' : 'Show all courses')
                            : (language === 'fr' ? 'Afficher uniquement les cours non terminés' : 'Show incomplete courses only')
                        }
                    >
                        <Filter size={14} />
                        <span className="hidden sm:inline">{language === 'fr' ? 'Non terminé' : 'Incomplete'}</span>
                    </button>
                    {/* Free content filter - only when level not unlocked */}
                    {!moduleHasFullAccess && (
                        <button
                            onClick={() => setShowFreeOnly(prev => !prev)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                showFreeOnly
                                    ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-500/30'
                                    : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                            }`}
                            title={showFreeOnly
                                ? (language === 'fr' ? 'Afficher tout le contenu' : 'Show all content')
                                : (language === 'fr' ? 'Afficher uniquement le contenu gratuit' : 'Show free content only')
                            }
                        >
                            <Filter size={14} />
                            <span className="hidden sm:inline">{language === 'fr' ? 'Gratuit' : 'Free'}</span>
                        </button>
                    )}
                    {/* Toggle stats visibility */}
                    <button
                        onClick={toggleCourseStats}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            showCourseStats
                                ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-500/30'
                                : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                        }`}
                        title={showCourseStats
                            ? (language === 'fr' ? 'Masquer les statistiques' : 'Hide statistics')
                            : (language === 'fr' ? 'Afficher les statistiques' : 'Show statistics')
                        }
                    >
                        {showCourseStats ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                        )}
                        <span className="hidden sm:inline">Stats</span>
                    </button>
                    </div>
                </div>

                {/* Loading courses */}
                {coursesLoading && (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                    </div>
                )}

                {/* Error loading courses */}
                {coursesError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center">
                        <p className="text-red-600 dark:text-red-400">
                            {language === 'fr' ? 'Erreur lors du chargement des cours' : 'Error loading courses'}
                        </p>
                    </div>
                )}

                {/* Courses list */}
                {!coursesLoading && !coursesError && (
                    <div>
                        {courses.length > 0 ? (
                            (() => {
                                const groups = groupedCourses();
                                if (!groups) {
                                    const message = showFavoritesOnly
                                        ? (language === 'fr' ? 'Aucun favori dans ce module. Cliquez sur l\u2019étoile d\u2019un cours pour l\u2019ajouter à vos favoris.' : 'No favorites in this module. Click the star on a course to add it to your favorites.')
                                        : showIncompleteOnly
                                            ? (language === 'fr' ? 'Tous les cours de ce module sont terminés !' : 'All courses in this module are completed!')
                                            : showFreeOnly
                                                ? (language === 'fr' ? 'Aucun contenu gratuit dans ce module.' : 'No free content in this module.')
                                                : null;
                                    if (!message) return null;
                                    return (
                                        <div className="bg-white dark:bg-[#121212] rounded-2xl p-8 border border-gray-200 dark:border-white/5 text-center">
                                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                                {message}
                                            </p>
                                        </div>
                                    );
                                }

                                // If ungrouped, show courses directly in a grid
                                if (groups.ungrouped) {
                                    return (
                                        <div className="flex flex-col gap-4">
                                            {renderPairedCards(groups.ungrouped)}
                                            {/* Module-level revision sheets */}
                                            {groupPdfs
                                                .filter(pdf => !pdf.lang || pdf.lang === language)
                                                .map(pdf => (
                                                <RevisionSheetBanner
                                                    key={pdf.groupName}
                                                    pdf={pdf}
                                                    moduleId={selectedModule}
                                                    language={language}
                                                    locked={!moduleHasFullAccess}
                                                />
                                            ))}
                                        </div>
                                    );
                                }

                                // Show grouped courses as accordions
                                return (
                                    <div className="space-y-3">
                                        {Object.entries(groups)
                                            .sort(([groupNameA], [groupNameB]) => groupNameA.localeCompare(groupNameB))
                                            .map(([groupName, coursesInGroup]) => {
                                            const { courseProgress, quizProgress, courseCount, quizCount } = getGroupProgressSplit(coursesInGroup);
                                            const groupTime = formatTotalTime(getGroupTotalTime(coursesInGroup));
                                            const isExpanded = expandedGroupByModule[getModuleKey(selectedModule)] === groupName;
                                            // If only one type exists, show single bar like before
                                            const hasBoth = courseProgress !== null && quizProgress !== null;

                                            return (
                                                <div
                                                    key={groupName}
                                                    ref={el => groupRefs.current[groupName] = el}
                                                    className="rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 scroll-mt-24"
                                                >
                                                    {/* Accordion Header */}
                                                    <button
                                                        onClick={() => toggleGroup(groupName)}
                                                        className={`w-full p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between transition-all duration-300 ${
                                                            isExpanded
                                                                ? 'bg-gray-100 dark:bg-[#1a1a1a]'
                                                                : 'bg-white dark:bg-[#121212] hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between w-full md:w-auto mb-3 md:mb-0">
                                                            <div className="flex items-center gap-4">
                                                                <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
                                                                    {groupName}
                                                                </h3>
                                                            </div>
                                                            <ChevronDown
                                                                className={`w-5 h-5 text-gray-400 md:hidden transition-transform duration-300 ${
                                                                    isExpanded ? 'rotate-180' : ''
                                                                }`}
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-4 w-full md:w-auto justify-start md:justify-end">
                                                            {/* Progress bars: separate Cours / Quiz when both exist */}
                                                            <div className={`flex items-center flex-wrap gap-3 flex-1 md:flex-none ${hasBoth ? 'gap-5' : ''}`}>
                                                                {hasBoth ? (
                                                                    <>
                                                                        {/* Course (reading) progress */}
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-[9px] font-black uppercase tracking-wider text-green-500 dark:text-green-400 whitespace-nowrap">
                                                                                {language === 'fr' ? 'Cours' : 'Course'}
                                                                            </span>
                                                                            <div className="w-16 md:w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                                <div
                                                                                    className={`h-full rounded-full transition-all duration-500 ${
                                                                                        courseProgress === 100 ? 'bg-green-500' : 'bg-green-400'
                                                                                    }`}
                                                                                    style={{ width: `${courseProgress}%` }}
                                                                                />
                                                                            </div>
                                                                            <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300 w-8 text-right">
                                                                                {courseProgress}%
                                                                            </span>
                                                                        </div>
                                                                        {/* Quiz progress */}
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-[9px] font-black uppercase tracking-wider text-purple-500 dark:text-purple-400 whitespace-nowrap">
                                                                                Quiz
                                                                            </span>
                                                                            <div className="w-16 md:w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                                <div
                                                                                    className={`h-full rounded-full transition-all duration-500 ${
                                                                                        quizProgress === 100 ? 'bg-purple-500' : 'bg-purple-400'
                                                                                    }`}
                                                                                    style={{ width: `${quizProgress}%` }}
                                                                                />
                                                                            </div>
                                                                            <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300 w-8 text-right">
                                                                                {quizProgress}%
                                                                            </span>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {/* Single progress bar (only courses or only quizzes in this group) */}
                                                                        {(() => {
                                                                            const progress = courseProgress ?? quizProgress ?? 0;
                                                                            const isQuizOnly = courseProgress === null;
                                                                            return (
                                                                                <>
                                                                                    <span className="text-sm font-bold text-gray-600 dark:text-gray-300">
                                                                                        {progress}%
                                                                                    </span>
                                                                                    <div className="w-full md:w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                                        <div
                                                                                            className={`h-full rounded-full transition-all duration-500 ${
                                                                                                progress === 100
                                                                                                    ? (isQuizOnly ? 'bg-purple-500' : 'bg-green-500')
                                                                                                    : progress >= 50
                                                                                                        ? 'bg-yellow-500'
                                                                                                        : 'bg-blue-500'
                                                                                            }`}
                                                                                            style={{ width: `${progress}%` }}
                                                                                        />
                                                                                    </div>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </>
                                                                )}
                                                                {groupTime && (
                                                                    <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                                                                        {groupTime}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* Chevron */}
                                                            <ChevronDown
                                                                className={`w-5 h-5 text-gray-400 hidden md:block transition-transform duration-300 ${
                                                                    isExpanded ? 'rotate-180' : ''
                                                                }`}
                                                            />
                                                        </div>
                                                    </button>

                                                    {/* Accordion Content */}
                                                    <div
                                                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                                                            isExpanded ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0'
                                                        }`}
                                                    >
                                                        <div className="p-4 bg-gray-50 dark:bg-[#0a0a0a]">
                                                            <div className="flex flex-col gap-3">
                                                                {renderPairedCards(coursesInGroup)}
                                                                {/* Revision sheet banner for this group */}
                                                                {groupPdfs.filter(pdf =>
                                                                    (!pdf.lang || pdf.lang === language) &&
                                                                    (pdf.groupName === groupName || pdf.labelFr === groupName || pdf.labelEn === groupName)
                                                                ).map(pdf => (
                                                                    <RevisionSheetBanner
                                                                        key={pdf.groupName}
                                                                        pdf={pdf}
                                                                        moduleId={selectedModule}
                                                                        language={language}
                                                                        locked={!moduleHasFullAccess}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {/* Global revision sheets (not matching any group) — shown at the end */}
                                        {(() => {
                                            const groupNames = Object.keys(groups);
                                            const globalPdfs = groupPdfs.filter(pdf =>
                                                (!pdf.lang || pdf.lang === language) &&
                                                !groupNames.some(gn => pdf.groupName === gn || pdf.labelFr === gn || pdf.labelEn === gn)
                                            );
                                            return globalPdfs.map(pdf => (
                                                <RevisionSheetBanner
                                                    key={pdf.groupName}
                                                    pdf={pdf}
                                                    moduleId={selectedModule}
                                                    language={language}
                                                    locked={!moduleHasFullAccess}
                                                />
                                            ));
                                        })()}
                                    </div>
                                );
                            })()
                        ) : (
                            <div className="bg-white dark:bg-[#121212] rounded-2xl p-8 border border-gray-200 dark:border-white/5 text-center">
                                <p className="text-gray-600 dark:text-gray-400">
                                    {language === 'fr'
                                        ? 'Aucun cours disponible pour ce module.'
                                        : 'No courses available for this module.'}
                                </p>
                            </div>
                        )}
                    </div>
                )}

            </div>

            {/* Quiz Mode Selection Modal — same component as exam start */}
            {showQuizModeModal && (
                <ModeSelectionModal
                    language={language}
                    onSelectMode={handleStartQuizWithMode}
                    onClose={() => setShowQuizModeModal(null)}
                />
            )}

            {/* Course Modal */}
            {showModal && selectedCourse && (
                <CourseModal
                    course={selectedCourse}
                    moduleId={selectedModule}
                    onClose={handleCloseModal}
                    language={language}
                />
            )}
        </div>
    );
};

export default CoursesTab;
